import { Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router-dom';
import { ScrollToTop } from './components/ScrollToTop';
import { CookieBanner } from './components/layout/CookieBanner';
import { HomePage } from './pages/HomePage';

const FeaturesPage = lazy(() => import('./pages/FeaturesPage').then(m => ({ default: m.FeaturesPage })));
const AboutPage = lazy(() => import('./pages/AboutPage').then(m => ({ default: m.AboutPage })));
const CompanyPage = lazy(() => import('./pages/CompanyPage').then(m => ({ default: m.CompanyPage })));
const ProxyPage = lazy(() => import('./pages/ProxyPage').then(m => ({ default: m.ProxyPage })));
const WhyMCPCloudPage = lazy(() => import('./pages/WhyMCPCloudPage').then(m => ({ default: m.WhyMCPCloudPage })));
const PricingPage = lazy(() => import('./pages/PricingPage').then(m => ({ default: m.PricingPage })));
const TermsPage = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.TermsPage })));
const CookiesPage = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.CookiesPage })));
const LegalPage = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.LegalPage })));
const DisclaimerPage = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.DisclaimerPage })));
const PrivacyPage = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.PrivacyPage })));
const SoloUsersPage = lazy(() => import('./pages/SoloUsersPage').then(m => ({ default: m.SoloUsersPage })));
const AllUsersPage = lazy(() => import('./pages/AllUsersPage').then(m => ({ default: m.AllUsersPage })));
const CompaniesPage = lazy(() => import('./pages/CompaniesPage').then(m => ({ default: m.CompaniesPage })));
const CorporateClientsPage = lazy(() => import('./pages/CorporateClientsPage').then(m => ({ default: m.CorporateClientsPage })));
const DevelopersPage = lazy(() => import('./pages/DevelopersPage').then(m => ({ default: m.DevelopersPage })));
const MCPDevelopersPage = lazy(() => import('./pages/MCPDevelopersPage').then(m => ({ default: m.MCPDevelopersPage })));
const BusinessesPage = lazy(() => import('./pages/BusinessesPage').then(m => ({ default: m.BusinessesPage })));
const ProfessionalsPage = lazy(() => import('./pages/ProfessionalsPage').then(m => ({ default: m.ProfessionalsPage })));
const SecurityGuardsPage = lazy(() => import('./pages/SecurityGuardsPage').then(m => ({ default: m.SecurityGuardsPage })));
const MCPSecurityPage = lazy(() => import('./pages/MCPSecurityPage').then(m => ({ default: m.MCPSecurityPage })));
const AuthenticationPage = lazy(() => import('./pages/AuthenticationPage').then(m => ({ default: m.AuthenticationPage })));
const GatewayPage = lazy(() => import('./pages/GatewayPage').then(m => ({ default: m.GatewayPage })));
const ServicesPage = lazy(() => import('./pages/ServicesPage').then(m => ({ default: m.ServicesPage })));
const CareersPage = lazy(() => import('./pages/CareersPage').then(m => ({ default: m.CareersPage })));
const JobPage = lazy(() => import('./pages/JobPage').then(m => ({ default: m.JobPage })));
const ObservabilityPage = lazy(() => import('./pages/ObservabilityPage').then(m => ({ default: m.ObservabilityPage })));
const InfrastructurePage = lazy(() => import('./pages/InfrastructurePage').then(m => ({ default: m.InfrastructurePage })));
const PaymentsPage = lazy(() => import('./pages/PaymentsPage').then(m => ({ default: m.PaymentsPage })));
const ContactPage = lazy(() => import('./pages/ContactPage').then(m => ({ default: m.ContactPage })));
const BlogPage = lazy(() => import('./pages/BlogPage').then(m => ({ default: m.BlogPage })));
const BlogRouter = lazy(() => import('./pages/BlogRouter').then(m => ({ default: m.BlogRouter })));
const FAQPage = lazy(() => import('./pages/FAQPage').then(m => ({ default: m.FAQPage })));
const MCPWebUI = lazy(() => import('./pages/MCPWebUI').then(m => ({ default: m.MCPWebUI })));
const MCPManagementPage = lazy(() => import('./pages/MCPManagementPage').then(m => ({ default: m.MCPManagementPage })));
const MultiMCPPage = lazy(() => import('./pages/MultiMCPPage').then(m => ({ default: m.MultiMCPPage })));
const AIAgentHostingPage = lazy(() => import('./pages/AIAgentHostingPage').then(m => ({ default: m.AIAgentHostingPage })));
const GovernancePage = lazy(() => import('./pages/GovernancePage').then(m => ({ default: m.GovernancePage })));
const GuardrailsPage = lazy(() => import('./pages/GuardrailsPage').then(m => ({ default: m.GuardrailsPage })));
const MCPHostingPage = lazy(() => import('./pages/MCPHostingPage').then(m => ({ default: m.MCPHostingPage })));
const RegistryPage = lazy(() => import('./pages/RegistryPage').then(m => ({ default: m.RegistryPage })));
const MCPSecurityScannerPage = lazy(() => import('./pages/MCPSecurityScannerPage').then(m => ({ default: m.MCPSecurityScannerPage })));
const ApiToMcpPage = lazy(() => import('./pages/ApiToMcpPage').then(m => ({ default: m.ApiToMcpPage })));
const MCPMarketPage = lazy(() => import('./pages/MCPMarketPage').then(m => ({ default: m.MCPMarketPage })));
const MCPToolsetsPage = lazy(() => import('./pages/MCPToolsetsPage').then(m => ({ default: m.MCPToolsetsPage })));

function RouteFallback() {
    return <main className="min-h-screen bg-[#151A20]" />;
}

/**
 * Main App component with routing configuration.
 */
function App() {
    return (
        <>
            <ScrollToTop />
            <Suspense fallback={<RouteFallback />}>
                <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/features" element={<FeaturesPage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/company" element={<CompanyPage />} />
                    <Route path="/mcp-proxy" element={<ProxyPage />} />
                    <Route path="/mcp-web-ui" element={<MCPWebUI />} />
                    <Route path="/mcp-api-bridge" element={<ApiToMcpPage />} />
                    <Route path="/mcp-management" element={<MCPManagementPage />} />
                    <Route path="/multi-mcp" element={<MultiMCPPage />} />
                    <Route path="/ai-agent-hosting" element={<AIAgentHostingPage />} />
                    <Route path="/mcp-governance" element={<GovernancePage />} />
                    <Route path="/mcp-registry" element={<RegistryPage />} />
                    <Route path="/mcp-market" element={<MCPMarketPage />} />
                    <Route path="/mcp-toolsets" element={<MCPToolsetsPage />} />
                    <Route path="/mcp-guardrails" element={<GuardrailsPage />} />
                    <Route path="/mcp-hosting" element={<MCPHostingPage />} />
                    <Route path="/why-mcp-cloud" element={<WhyMCPCloudPage />} />
                    <Route path="/pricing" element={<PricingPage />} />
                    <Route path="/faq" element={<FAQPage />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/cookies" element={<CookiesPage />} />
                    <Route path="/legal" element={<LegalPage />} />
                    <Route path="/disclaimer" element={<DisclaimerPage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/mcp-security" element={<MCPSecurityPage />} />
                    <Route path="/mcp-security-scanner" element={<MCPSecurityScannerPage />} />
                    <Route path="/security-guards" element={<SecurityGuardsPage />} />
                    <Route path="/mcp-authentication" element={<AuthenticationPage />} />
                    <Route path="/mcp-gateway" element={<GatewayPage />} />
                    <Route path="/mcp-observability" element={<ObservabilityPage />} />
                    <Route path="/mcp-infrastructure" element={<InfrastructurePage />} />
                    <Route path="/mcp-payments" element={<PaymentsPage />} />
                    <Route path="/services" element={<ServicesPage />} />
                    <Route path="/individuals" element={<SoloUsersPage />} />
                    <Route path="/visionaries" element={<AllUsersPage />} />
                    <Route path="/developers" element={<DevelopersPage />} />
                    <Route path="/mcp-developers" element={<MCPDevelopersPage />} />
                    <Route path="/businesses" element={<BusinessesPage />} />
                    <Route path="/professionals" element={<ProfessionalsPage />} />
                    <Route path="/companies" element={<CompaniesPage />} />
                    <Route path="/corporate_clients" element={<CorporateClientsPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/careers" element={<CareersPage />} />
                    <Route path="/careers/:slug" element={<JobPage />} />
                    <Route path="/blog" element={<BlogPage />} />
                    <Route path="/blog/:slug" element={<BlogRouter />} />
                </Routes>
            </Suspense>
            <CookieBanner />
        </>
    );
}

export default App;
